import { useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import SectionLabel from "../ui/SectionLabel";
import CountUp from "../ui/CountUp";

export default function CloserNew() {
  const sectionRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const gsapModule = await import("gsap");
      const gsap = gsapModule.default;
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");
      gsap.registerPlugin(ScrollTrigger);
      if (cancelled || !sectionRef.current) return;

      gsap.context(() => {
        const heading = sectionRef.current.querySelector(".closer-heading");
        if (heading) {
          gsap.from(heading, {
            y: 50,
            opacity: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: heading, start: "top 82%" },
          });
        }
        const body = sectionRef.current.querySelector(".closer-body");
        if (body) {
          gsap.from(body, {
            y: 35,
            opacity: 0,
            duration: 0.8,
            ease: "power3.out",
            delay: 0.2,
            scrollTrigger: { trigger: body, start: "top 85%" },
          });
        }
      }, sectionRef);
    })();

    return () => { cancelled = true; };
  }, []);

  const handleMove = useCallback((e) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    btn.style.transform = `translate(${x * 0.15}px, ${y * 0.25}px) scale(1.02)`;
  }, []);

  const handleLeave = useCallback(() => {
    if (btnRef.current) btnRef.current.style.transform = "translate(0, 0) scale(1)";
  }, []);

  return (
    <section
      id="audit"
      ref={sectionRef}
      style={{
        position: "relative",
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-base)",
        overflow: "hidden",
        padding: "var(--section-padding-y) var(--container-padding)",
      }}
    >
      {/* Aurora blobs */}
      <div className="closer-aurora">
        <div className="closer-aurora-1" />
        <div className="closer-aurora-2" />
        <div className="closer-aurora-3" />
      </div>

      {/* Grid background */}
      <div className="closer-grid" />

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", maxWidth: "720px" }}>
        <SectionLabel text="LET'S BEGIN" />

        <h2 className="closer-heading">
          Ready to see what's{" "}
          <span className="closer-heading-accent">costing you?</span>
        </h2>

        <div className="closer-body">
          <p className="closer-desc">
            30-minute audit. We show you 3-5 areas where you're bleeding time and money.
            <br />
            <span style={{ color: "var(--text-muted)" }}>
              No pitch deck. No sales script. Just numbers.
            </span>
          </p>

          {/* CTA with glow ring */}
          <div className="closer-cta-wrap">
            <div className="closer-cta-glow" />
            <a
              ref={btnRef}
              href="/resource-optimizer/book"
              className="btn-primary closer-cta"
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
            >
              Book your free audit
              <svg width="18" height="18" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 6 }}>
                <path d="M1 7h11M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          {/* Trust stats */}
          <div className="closer-stats">
            <div className="closer-stat">
              <span className="closer-stat-value">
                <CountUp target={14} />
              </span>
              <span className="closer-stat-label">organisations in active transformation</span>
            </div>
            <div className="closer-stat-divider" />
            <div className="closer-stat">
              <span className="closer-stat-value">
                <CountUp target={12} prefix="₹" suffix="Cr+" />
              </span>
              <span className="closer-stat-label">revenue saved to date</span>
            </div>
            <div className="closer-stat-divider" />
            <div className="closer-stat">
              <span className="closer-stat-value">
                <CountUp target={100} suffix="+" />
              </span>
              <span className="closer-stat-label">workflows automated</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ── Aurora ── */
        .closer-aurora {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .closer-aurora-1 {
          position: absolute;
          width: 700px;
          height: 700px;
          background: radial-gradient(circle, rgba(40,180,174,0.14) 0%, transparent 70%);
          top: 30%;
          left: 50%;
          transform: translate(-50%, -50%);
          filter: blur(100px);
          animation: closer-drift-1 12s ease-in-out infinite;
        }

        .closer-aurora-2 {
          position: absolute;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(40,120,220,0.08) 0%, transparent 70%);
          top: 60%;
          left: 20%;
          filter: blur(100px);
          animation: closer-drift-2 15s ease-in-out infinite;
        }

        .closer-aurora-3 {
          position: absolute;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(93,224,219,0.08) 0%, transparent 70%);
          top: 40%;
          right: 10%;
          filter: blur(100px);
          animation: closer-drift-3 18s ease-in-out infinite;
        }

        @keyframes closer-drift-1 {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-45%, -45%) scale(1.15); }
        }

        @keyframes closer-drift-2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(40px, -30px); }
        }

        @keyframes closer-drift-3 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-30px, 20px); }
        }

        .closer-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(40,180,174,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(40,180,174,0.02) 1px, transparent 1px);
          background-size: 80px 80px;
          mask-image: radial-gradient(ellipse 60% 50% at 50% 50%, black 10%, transparent 70%);
          -webkit-mask-image: radial-gradient(ellipse 60% 50% at 50% 50%, black 10%, transparent 70%);
          pointer-events: none;
        }

        /* ── Heading ── */
        .closer-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          font-size: clamp(34px, 5vw, 60px);
          line-height: 1.08;
          color: var(--text-primary);
          letter-spacing: -0.035em;
          margin: 8px 0 24px;
        }

        .closer-heading-accent {
          background: linear-gradient(135deg, var(--accent) 0%, #5de0db 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .closer-desc {
          font-family: 'Inter', sans-serif;
          font-size: 18px;
          line-height: 1.75;
          color: var(--text-secondary);
          margin-bottom: 40px;
        }

        /* ── CTA ── */
        .closer-cta-wrap {
          position: relative;
          display: inline-block;
          margin-bottom: 48px;
        }

        .closer-cta-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(40,180,174,0.15) 0%, transparent 60%);
          filter: blur(40px);
          pointer-events: none;
          animation: cta-glow-pulse 3s ease-in-out infinite;
        }

        @keyframes cta-glow-pulse {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
        }

        .closer-cta {
          position: relative;
          padding: 22px 56px !important;
          font-size: 18px !important;
          border-radius: 16px !important;
          box-shadow: 0 0 0 1px rgba(40,180,174,0.3),
                      0 8px 40px rgba(40,180,174,0.25),
                      0 0 120px rgba(40,180,174,0.08) !important;
          transition: transform 0.25s cubic-bezier(0.33, 1, 0.68, 1),
                      box-shadow 0.3s ease,
                      background 0.3s ease !important;
          will-change: transform;
          z-index: 1;
        }

        .closer-cta:hover {
          box-shadow: 0 0 0 1px rgba(40,180,174,0.5),
                      0 12px 56px rgba(40,180,174,0.35),
                      0 0 200px rgba(40,180,174,0.12) !important;
        }

        /* ── Stats ── */
        .closer-stats {
          display: flex;
          align-items: center;
          justify-content: center;
          padding-top: 28px;
          border-top: 1px solid var(--border-default);
          gap: 0;
        }

        .closer-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 0 36px;
        }

        .closer-stat-divider {
          width: 1px;
          height: 40px;
          background: linear-gradient(180deg, transparent, var(--border-default), transparent);
          flex-shrink: 0;
        }

        .closer-stat-value {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          font-size: 30px;
          color: var(--text-primary);
          line-height: 1;
          letter-spacing: -0.03em;
        }

        .closer-stat-label {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 500;
          color: var(--text-muted);
          text-align: center;
          max-width: 140px;
        }

        /* ── Light ── */
        .light .closer-aurora-1 { background: radial-gradient(circle, rgba(40,180,174,0.08) 0%, transparent 70%); }
        .light .closer-aurora-2 { background: radial-gradient(circle, rgba(40,120,220,0.04) 0%, transparent 70%); }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .closer-stats {
            flex-direction: column;
            gap: 20px;
          }
          .closer-stat-divider {
            width: 40px;
            height: 1px;
          }
          .closer-stat {
            padding: 0;
          }
          .closer-cta {
            padding: 18px 40px !important;
            font-size: 16px !important;
          }
        }
      `}</style>
    </section>
  );
}
